import { ChartSetList } from '../types';
import * as db from '../utils/db';
import * as drive from '../utils/drive';

interface SetListCRUDDeps {
  setLists: ChartSetList[];
  setSetLists: (setLists: ChartSetList[]) => void;
  activeSetListId: string | null;
  setActiveSetListId: (id: string | null) => void;
  isGapiReady: boolean;
  userProfile: any;
}

const generateSetListId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export function useSetListCRUD(deps: SetListCRUDDeps) {
  const { setLists, setSetLists, activeSetListId, setActiveSetListId, isGapiReady, userProfile } = deps;

  const handleCreateSetList = async (setListData: Omit<ChartSetList, 'id' | 'createdAt' | 'lastModified'>) => {
    const newSetList: ChartSetList = {
      ...setListData,
      id: generateSetListId(),
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };

    const updatedSetLists = [...setLists, newSetList];
    setSetLists(updatedSetLists);

    try {
      await db.saveSetLists(updatedSetLists);

      if (isGapiReady && userProfile) {
        try {
          await drive.saveCollectionsToDrive(updatedSetLists);
        } catch (error) {
          console.error('Failed to sync set lists to cloud:', error);
        }
      }
    } catch (error) {
      console.error('Failed to save set list:', error);
    }
  };

  const handleUpdateSetList = async (id: string, updates: Partial<ChartSetList>) => {
    const updatedSetLists = setLists.map(setList =>
      setList.id === id
        ? { ...setList, ...updates, lastModified: new Date().toISOString() }
        : setList
    );

    setSetLists(updatedSetLists);

    try {
      await db.saveSetLists(updatedSetLists);

      if (isGapiReady && userProfile) {
        try {
          await drive.saveCollectionsToDrive(updatedSetLists);
        } catch (error) {
          console.error('Failed to sync set lists to cloud:', error);
        }
      }
    } catch (error) {
      console.error('Failed to update set list:', error);
    }
  };

  const handleDeleteSetList = async (id: string) => {
    const setList = setLists.find(c => c.id === id);
    if (!setList) return;

    if (window.confirm(`Are you sure you want to delete the set list "${setList.name}"? This will not delete the charts themselves.`)) {
      const updatedSetLists = setLists.filter(c => c.id !== id);
      setSetLists(updatedSetLists);

      if (activeSetListId === id) {
        setActiveSetListId(null);
      }

      try {
        await db.saveSetLists(updatedSetLists);

        if (isGapiReady && userProfile) {
          try {
            await drive.saveCollectionsToDrive(updatedSetLists);
          } catch (error) {
            console.error('Failed to sync set lists to cloud:', error);
          }
        }
      } catch (error) {
        console.error('Failed to delete set list:', error);
      }
    }
  };

  const handleAddChartToSetList = async (setListId: string, chartTitle: string) => {
    const updatedSetLists = setLists.map(setList =>
      setList.id === setListId
        ? {
            ...setList,
            chartTitles: [...setList.chartTitles, chartTitle],
            lastModified: new Date().toISOString()
          }
        : setList
    );

    setSetLists(updatedSetLists);

    try {
      await db.saveSetLists(updatedSetLists);

      if (isGapiReady && userProfile) {
        try {
          await drive.saveCollectionsToDrive(updatedSetLists);
        } catch (error) {
          console.error('Failed to sync set lists to cloud:', error);
        }
      }
    } catch (error) {
      console.error('Failed to add chart to set list:', error);
    }
  };

  const handleRemoveChartFromSetList = async (setListId: string, chartTitle: string) => {
    const updatedSetLists = setLists.map(setList =>
      setList.id === setListId
        ? {
            ...setList,
            chartTitles: setList.chartTitles.filter(title => title !== chartTitle),
            lastModified: new Date().toISOString()
          }
        : setList
    );

    setSetLists(updatedSetLists);

    try {
      await db.saveSetLists(updatedSetLists);

      if (isGapiReady && userProfile) {
        try {
          await drive.saveCollectionsToDrive(updatedSetLists);
        } catch (error) {
          console.error('Failed to sync set lists to cloud:', error);
        }
      }
    } catch (error) {
      console.error('Failed to remove chart from set list:', error);
    }
  };

  return {
    handleCreateSetList,
    handleUpdateSetList,
    handleDeleteSetList,
    handleAddChartToSetList,
    handleRemoveChartFromSetList,
  };
}

export type UseSetListCRUDReturn = ReturnType<typeof useSetListCRUD>;
